/**
 * ConectividadeHost — o modal de "é a sua internet, não o app".
 *
 * Montado no App.jsx, fora das rotas, como o AppUpdateModal: precisa existir
 * no login, no funil e em toda aba. Lê o estado de telemetria/conectividade e
 * mostra um de dois avisos:
 *  - SEM CONEXÃO: o aparelho está offline. Some sozinho quando a rede volta.
 *  - INSTÁVEL: o aparelho diz online, mas as chamadas estão caindo no
 *    transporte. É o caso em que a pessoa vê "não carregou" e culpa o app.
 *
 * Regras de não incomodar:
 *  - offline espera 1,5s antes de aparecer — um piscar de rede ao trocar de
 *    Wi-Fi para dados não merece um modal;
 *  - cada aviso aparece UMA vez por episódio; "Entendi" cala até o próximo;
 *  - o instável ainda respeita um intervalo mínimo entre avisos, porque uma
 *    rede ruim gera episódios em sequência.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../design/components/Modal/Modal';
import { assinar, lerEstado } from './conectividade';

const ESPERA_OFFLINE_MS = 1500;

export default function ConectividadeHost() {
  const { t } = useTranslation(['erros']);
  const [aviso, setAviso] = useState(null); // null | 'sem_conexao' | 'instavel'
  const caladoOffline = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    const aplicar = (estado) => {
      clearTimeout(timer.current);

      if (!estado.online) {
        if (caladoOffline.current) return;
        timer.current = setTimeout(() => setAviso('sem_conexao'), ESPERA_OFFLINE_MS);
        return;
      }

      // Rede voltou: o aviso de offline sai e o próximo episódio pode avisar.
      caladoOffline.current = false;
      setAviso((atual) => (atual === 'sem_conexao' ? null : atual));

      /* O aviso de "instável" foi DESLIGADO (19/09): mesmo com limiares mais
         altos ele ainda aparecia com a rede boa o bastante para usar o app.
         O estado `instavel` continua alimentando a telemetria
         (conexao_instavel em errosDoApp), só não vira modal. Fica apenas o
         "sem conexão", que é o único caso que a pessoa precisa saber. */
    };

    aplicar(lerEstado());
    const desassinar = assinar(aplicar);
    return () => {
      desassinar();
      clearTimeout(timer.current);
    };
  }, []);

  if (!aviso) return null;

  const fechar = () => {
    if (aviso === 'sem_conexao') caladoOffline.current = true;
    setAviso(null);
  };

  return (
    <Modal
      aberto
      titulo={t(`erros:rede.${aviso}.titulo`)}
      corpo={t(`erros:rede.${aviso}.corpo`)}
      acaoRotulo={t('erros:rede.entendi')}
      onAcao={fechar}
      onCancelar={fechar}
      cancelarRotulo={t('erros:rede.fechar')}
      data-conectividade={aviso}
    />
  );
}
